import { ChartBinSize } from '@frontend/chart/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getDefaultInsightRange, isValidIsoDate, parseChartBinSize } from './insight-default-range';
import { InsightDetailFiltersValue, buildInsightEntityParams } from './insight-detail-filters.context';
import { INSIGHT_ENTITY_FILTER_PARAMS, INSIGHT_FILTER_PARAM, InsightEntityFilterKey, InsightFilterConfig } from './insight-filter-config';
import { applyBinSizeRangeGuards } from './insight-filter-guards';

const readOptionalParam = (searchParams: URLSearchParams, key: string): string | null => {
    const value = searchParams.get(key);
    return value && value.trim() ? value : null;
};

const setOrDeleteParam = (params: URLSearchParams, key: string, value: string | null) => {
    if (value) {
        params.set(key, value);
    } else {
        params.delete(key);
    }
};

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

/** Always merge into the latest URL so entity clears cannot race with date writes. */
const writeDateParams = (setSearchParams: SetSearchParams, next: { after: string; before: string; binSize: ChartBinSize }) => {
    setSearchParams(
        (prev) => {
            const params = new URLSearchParams(prev);
            params.set(INSIGHT_FILTER_PARAM.AFTER, next.after);
            params.set(INSIGHT_FILTER_PARAM.BEFORE, next.before);
            params.set(INSIGHT_FILTER_PARAM.BIN_SIZE, next.binSize);
            return params;
        },
        { replace: true }
    );
};

export const useInsightDetailFiltersState = (filterConfig: InsightFilterConfig | undefined): InsightDetailFiltersValue => {
    const [searchParams, setSearchParams] = useSearchParams();

    const entityFilters = filterConfig?.entityFilters ?? [];
    const showDateRange = filterConfig?.showDateRange ?? false;

    const spotId = entityFilters.includes('spot') ? readOptionalParam(searchParams, INSIGHT_FILTER_PARAM.SPOT_ID) : null;
    const productId = entityFilters.includes('product') ? readOptionalParam(searchParams, INSIGHT_FILTER_PARAM.PRODUCT_ID) : null;
    const userId = entityFilters.includes('user') ? readOptionalParam(searchParams, INSIGHT_FILTER_PARAM.USER_ID) : null;
    const iotType = entityFilters.includes('iotType') ? readOptionalParam(searchParams, INSIGHT_FILTER_PARAM.IOT_TYPE) : null;
    const iotBrand = entityFilters.includes('iotBrand') ? readOptionalParam(searchParams, INSIGHT_FILTER_PARAM.IOT_BRAND) : null;

    // Read date params as primitives so unrelated URL changes (e.g. `details` for detail modals)
    // do not recreate filter state and refetch insight charts.
    const urlAfter = searchParams.get(INSIGHT_FILTER_PARAM.AFTER);
    const urlBefore = searchParams.get(INSIGHT_FILTER_PARAM.BEFORE);
    const urlBinRaw = searchParams.get(INSIGHT_FILTER_PARAM.BIN_SIZE);

    const resolved = useMemo(() => {
        if (!filterConfig?.enabled || !filterConfig.showDateRange) {
            return null;
        }

        const defaults = getDefaultInsightRange(filterConfig.defaultRange);
        const after = isValidIsoDate(urlAfter) ? urlAfter : defaults.after;
        const before = isValidIsoDate(urlBefore) ? urlBefore : defaults.before;
        const preferredBinSize = parseChartBinSize(urlBinRaw) ?? filterConfig.defaultBinSize;
        const allowed = filterConfig.editableBinSizes.length > 0 ? filterConfig.editableBinSizes : [filterConfig.defaultBinSize];

        return applyBinSizeRangeGuards({
            after,
            before,
            preferredBinSize,
            allowedBinSizes: allowed,
            lockBinSize: filterConfig.lockBinSize
        });
    }, [filterConfig, urlAfter, urlBefore, urlBinRaw]);

    // Keep URL in sync when guards rewrite after/before/bin_size (invalid deep links).
    useEffect(() => {
        if (!filterConfig?.enabled || !filterConfig.showDateRange || !resolved) return;
        if (!urlAfter && !urlBefore && !urlBinRaw) {
            return;
        }
        if (urlAfter === resolved.after && urlBefore === resolved.before && urlBinRaw === resolved.binSize) {
            return;
        }
        writeDateParams(setSearchParams, resolved);
    }, [filterConfig?.enabled, filterConfig?.showDateRange, resolved, urlAfter, urlBefore, urlBinRaw, setSearchParams]);

    const setRange = useCallback(
        (after: string, before: string) => {
            if (!filterConfig?.enabled || !filterConfig.showDateRange || !resolved) return;
            const allowed = filterConfig.editableBinSizes.length > 0 ? filterConfig.editableBinSizes : [filterConfig.defaultBinSize];
            const guarded = applyBinSizeRangeGuards({
                after,
                before,
                preferredBinSize: resolved.binSize,
                allowedBinSizes: allowed,
                lockBinSize: filterConfig.lockBinSize
            });
            writeDateParams(setSearchParams, guarded);
        },
        [filterConfig, resolved, setSearchParams]
    );

    const setBinSize = useCallback(
        (binSize: ChartBinSize) => {
            if (!filterConfig?.enabled || !filterConfig.showDateRange || !resolved || filterConfig.lockBinSize) return;
            if (!filterConfig.editableBinSizes.includes(binSize)) return;

            const guarded = applyBinSizeRangeGuards({
                after: resolved.after,
                before: resolved.before,
                preferredBinSize: binSize,
                allowedBinSizes: filterConfig.editableBinSizes,
                lockBinSize: false
            });
            writeDateParams(setSearchParams, guarded);
        },
        [filterConfig, resolved, setSearchParams]
    );

    const writeEntityParam = useCallback(
        (key: (typeof INSIGHT_ENTITY_FILTER_PARAMS)[number], value: string | null, allowed: InsightEntityFilterKey) => {
            if (!filterConfig?.enabled || !filterConfig.entityFilters.includes(allowed)) return;
            const next = value && value.trim() ? value : null;
            setSearchParams(
                (prev) => {
                    const current = prev.get(key);
                    if ((current ?? null) === next) {
                        return prev;
                    }
                    const params = new URLSearchParams(prev);
                    setOrDeleteParam(params, key, next);
                    return params;
                },
                { replace: true }
            );
        },
        [filterConfig, setSearchParams]
    );

    const setSpotId = useCallback((value: string | null) => writeEntityParam(INSIGHT_FILTER_PARAM.SPOT_ID, value, 'spot'), [writeEntityParam]);
    const setProductId = useCallback((value: string | null) => writeEntityParam(INSIGHT_FILTER_PARAM.PRODUCT_ID, value, 'product'), [writeEntityParam]);
    const setUserId = useCallback((value: string | null) => writeEntityParam(INSIGHT_FILTER_PARAM.USER_ID, value, 'user'), [writeEntityParam]);
    const setIotType = useCallback((value: string | null) => writeEntityParam(INSIGHT_FILTER_PARAM.IOT_TYPE, value, 'iotType'), [writeEntityParam]);
    const setIotBrand = useCallback((value: string | null) => writeEntityParam(INSIGHT_FILTER_PARAM.IOT_BRAND, value, 'iotBrand'), [writeEntityParam]);

    const entityParams = useMemo(
        () =>
            buildInsightEntityParams({
                entityFilters,
                spotId,
                productId,
                userId,
                iotType,
                iotBrand
            }),
        [entityFilters, spotId, productId, userId, iotType, iotBrand]
    );

    return useMemo((): InsightDetailFiltersValue => {
        if (!filterConfig?.enabled) {
            return {
                active: false,
                showDateRange: false,
                after: '',
                before: '',
                binSize: ChartBinSize.DAY,
                lockBinSize: true,
                editableBinSizes: [],
                binSizeUpgraded: false,
                rangeClamped: false,
                entityFilters: [],
                spotId: null,
                productId: null,
                userId: null,
                iotType: null,
                iotBrand: null,
                entityParams: {},
                setRange: () => undefined,
                setBinSize: () => undefined,
                setSpotId: () => undefined,
                setProductId: () => undefined,
                setUserId: () => undefined,
                setIotType: () => undefined,
                setIotBrand: () => undefined
            };
        }

        return {
            active: true,
            showDateRange,
            after: resolved?.after ?? '',
            before: resolved?.before ?? '',
            binSize: resolved?.binSize ?? filterConfig.defaultBinSize,
            lockBinSize: filterConfig.lockBinSize,
            editableBinSizes: filterConfig.editableBinSizes,
            binSizeUpgraded: resolved?.binSizeUpgraded ?? false,
            rangeClamped: resolved?.rangeClamped ?? false,
            entityFilters,
            spotId,
            productId,
            userId,
            iotType,
            iotBrand,
            entityParams,
            setRange,
            setBinSize,
            setSpotId,
            setProductId,
            setUserId,
            setIotType,
            setIotBrand
        };
    }, [
        filterConfig,
        showDateRange,
        resolved,
        entityFilters,
        spotId,
        productId,
        userId,
        iotType,
        iotBrand,
        entityParams,
        setRange,
        setBinSize,
        setSpotId,
        setProductId,
        setUserId,
        setIotType,
        setIotBrand
    ]);
};
